/**
 * plugins/flowchart.ts — Fluxograma (div.flowchart)
 *
 * Modos de contexto:
 *   node     — clicou em .fc-node: editar tipo/texto, cores custom, estrutura
 *   decision — clicou em .fc-decision: editar texto e cores
 *   lane     — clicou em .fc-lane: editar label
 *   chart    — clicou no fundo do .flowchart: adicionar elementos no final
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, rgbToHex, closestInside, directChildOf } from '../types';

type FcNodeType =
    | 'fc-start' | 'fc-end' | 'fc-process'
    | 'fc-process-blue' | 'fc-process-green' | 'fc-process-red' | 'fc-process-warn'
    | 'fc-io' | 'fc-subprocess' | 'fc-document';

type FcMode = 'node' | 'decision' | 'lane' | 'chart';

const FC_NODE_TYPES: FcNodeType[] = [
    'fc-start', 'fc-end', 'fc-process',
    'fc-process-blue', 'fc-process-green', 'fc-process-red', 'fc-process-warn',
    'fc-io', 'fc-subprocess', 'fc-document',
];

const FC_NODE_LABELS: Record<FcNodeType, string> = {
    'fc-start':         'Início',
    'fc-end':           'Fim',
    'fc-process':       'Processo',
    'fc-process-blue':  'Processo Azul',
    'fc-process-green': 'Processo Verde',
    'fc-process-red':   'Processo Vermelho',
    'fc-process-warn':  'Processo Aviso',
    'fc-io':            'Entrada / Saída',
    'fc-subprocess':    'Sub-processo',
    'fc-document':      'Documento',
};

export class FlowchartPlugin implements VtbPlugin {
    readonly selectorHints = ['div.flowchart', '.flowchart', '[data-vtb-id="flowchart"]'];
    readonly panelId = 'vtb-flowchart';

    // ─── Detecção ─────────────────────────────────────────────────────────────

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const chart = closestInside(target, visualEditor, e => 
            e.classList.contains('flowchart') || e.dataset.vtbId === 'flowchart'
        );
        if (!chart) return null;

        // Decisão (prioridade > node pois contém <span> interno)
        const decision = closestInside(target, chart, e => 
            e.classList.contains('fc-decision') || e.dataset.vtbId === 'decision'
        );
        if (decision) {
            return { type: this.panelId, label: 'Fluxo: Decisão', el: decision, extra: { mode: 'decision' as FcMode, chart } };
        }

        // Raia
        const lane = closestInside(target, chart, e => 
            e.classList.contains('fc-lane') || e.dataset.vtbId === 'lane'
        );
        if (lane) {
            return { type: this.panelId, label: 'Fluxo: Raia', el: lane, extra: { mode: 'lane' as FcMode, chart } };
        }

        // Nó (inclui nós dentro de branches)
        const node = closestInside(target, chart, e => 
            e.classList.contains('fc-node') || e.dataset.vtbId === 'node'
        );
        if (node) {
            const branch = closestInside(node, chart, e => 
                e.classList.contains('fc-branch') || e.dataset.vtbId === 'branch'
            );
            const typeCls = FC_NODE_TYPES.find(t => node.classList.contains(t));
            const label = typeCls ? FC_NODE_LABELS[typeCls] : 'Fluxo: Nó';
            return { type: this.panelId, label: 'Fluxo: ' + label, el: node, extra: { mode: 'node' as FcMode, chart, branch: branch ?? null } };
        }

        // Fundo do chart
        return { type: this.panelId, label: 'Fluxograma', el: chart, extra: { mode: 'chart' as FcMode, chart } };
    }

    // ─── Ativação do painel ───────────────────────────────────────────────────

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const mode = ctx.extra?.mode as FcMode;

        const rowMap: Record<string, FcMode[]> = {
            'vtb-fc-node-row':     ['node'],
            'vtb-fc-decision-row': ['decision'],
            'vtb-fc-lane-row':     ['lane'],
            'vtb-fc-color-row':    ['node', 'decision'],
            'vtb-fc-struct-row':   ['node', 'decision', 'lane'],
            'vtb-fc-chart-row':    ['chart'],
        };

        for (const [id, modes] of Object.entries(rowMap)) {
            const el = registry.q<HTMLElement>(id);
            if (el) el.style.display = modes.includes(mode) ? 'flex' : 'none';
        }

        // Chip de contexto
        const chip = registry.q<HTMLElement>('vtb-fc-ctx');
        if (chip) chip.textContent = this._modeLabel(ctx);

        if (mode === 'node')     this._activateNode(ctx, registry);
        if (mode === 'decision') this._activateDecision(ctx, registry);
        if (mode === 'lane')     this._activateLane(ctx, registry);
    }

    private _activateNode(ctx: SmartCtx, registry: IVtbRegistry): void {
        const node = ctx.el;
        const type = FC_NODE_TYPES.find(t => node.classList.contains(t)) ?? 'fc-process';
        const typeSel = registry.q<HTMLSelectElement>('vtb-fc-type');
        if (typeSel) typeSel.value = type;

        // Texto: pega do <span> interno (fc-io) ou textContent direto
        const textEl = node.querySelector<HTMLElement>('span');
        const text   = textEl ? textEl.textContent ?? '' : node.textContent ?? '';
        this._setVal(registry, 'vtb-fc-text', text.trim());

        // Cores
        this._setColor(registry, 'vtb-fc-bg',     rgbToHex(node.style.backgroundColor) || '#ffffff');
        this._setColor(registry, 'vtb-fc-border',  rgbToHex(node.style.borderColor)     || '#cbd5e1');
        this._setColor(registry, 'vtb-fc-fg',      rgbToHex(node.style.color)           || '#1e293b');
    }

    private _activateDecision(ctx: SmartCtx, registry: IVtbRegistry): void {
        const dec  = ctx.el;
        const span = dec.querySelector<HTMLElement>('span');
        this._setVal(registry, 'vtb-fc-dec-text', span?.textContent?.trim() ?? '');

        this._setColor(registry, 'vtb-fc-bg',     rgbToHex(dec.style.backgroundColor)                    || '#fffbeb');
        this._setColor(registry, 'vtb-fc-border',  rgbToHex(dec.style.borderColor)                        || '#f59e0b');
        this._setColor(registry, 'vtb-fc-fg',      rgbToHex(span?.style.color ?? '')                      || '#78350f');
    }

    private _activateLane(ctx: SmartCtx, registry: IVtbRegistry): void {
        const label = ctx.el.querySelector<HTMLElement>('.fc-lane-label');
        this._setVal(registry, 'vtb-fc-lane-label', label?.textContent?.trim() ?? '');
    }

    // ─── Setup de eventos (chamado 1x no init) ────────────────────────────────

    setup(registry: IVtbRegistry): void {
        // Aplicar
        registry.q('vtb-fc-node-apply')   ?.addEventListener('click', () => this._applyNode(registry));
        registry.q('vtb-fc-dec-apply')    ?.addEventListener('click', () => this._applyDecision(registry));
        registry.q('vtb-fc-lane-apply')   ?.addEventListener('click', () => this._applyLane(registry));
        registry.q('vtb-fc-color-apply')  ?.addEventListener('click', () => this._applyColors(registry));
        registry.q('vtb-fc-color-reset')  ?.addEventListener('click', () => this._resetColors(registry));

        // Live color preview
        registry.q<HTMLInputElement>('vtb-fc-bg')    ?.addEventListener('input', () => this._liveColors(registry));
        registry.q<HTMLInputElement>('vtb-fc-border') ?.addEventListener('input', () => this._liveColors(registry));
        registry.q<HTMLInputElement>('vtb-fc-fg')    ?.addEventListener('input', () => this._liveColors(registry));

        // Estrutura (para elementos já selecionados)
        registry.q('vtb-fc-add-below')    ?.addEventListener('click', () => this._addBelow(registry));
        registry.q('vtb-fc-add-decision') ?.addEventListener('click', () => this._addDecisionBlock(registry));
        registry.q('vtb-fc-add-lane')     ?.addEventListener('click', () => this._addLane(registry));
        registry.q('vtb-fc-move-up')      ?.addEventListener('click', () => this._move(registry, -1));
        registry.q('vtb-fc-move-down')    ?.addEventListener('click', () => this._move(registry,  1));
        registry.q('vtb-fc-duplicate')    ?.addEventListener('click', () => this._duplicate(registry));
        registry.q('vtb-fc-del')          ?.addEventListener('click', () => this._delete(registry));

        // Ações globais (modo chart — adicionar no final)
        registry.q('vtb-fc-chart-add-node')     ?.addEventListener('click', () => this._addAtEnd(registry, 'node'));
        registry.q('vtb-fc-chart-add-decision') ?.addEventListener('click', () => this._addAtEnd(registry, 'decision'));
        registry.q('vtb-fc-chart-add-lane')     ?.addEventListener('click', () => this._addAtEnd(registry, 'lane'));
    }

    // ─── Operações de Nó ──────────────────────────────────────────────────────

    private _applyNode(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const node = ctx.el;
        const type = registry.q<HTMLSelectElement>('vtb-fc-type')?.value as FcNodeType;
        const text = registry.q<HTMLInputElement>('vtb-fc-text')?.value?.trim() ?? '';

        // Troca tipo — remove cores inline ao mudar preset
        FC_NODE_TYPES.forEach(t => node.classList.remove(t));
        if (type) node.classList.add(type);
        node.style.removeProperty('background-color');
        node.style.removeProperty('border-color');
        node.style.removeProperty('color');

        // fc-io precisa de <span> interno (corrige o skewX do CSS)
        if (type === 'fc-io') {
            node.innerHTML = '<span>' + this._esc(text) + '</span>';
        } else {
            node.textContent = text;
        }
        registry.sync();
    }

    private _applyDecision(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'decision') return;
        const dec  = ctx.el;
        const text = registry.q<HTMLInputElement>('vtb-fc-dec-text')?.value?.trim() ?? '';
        let span   = dec.querySelector<HTMLElement>('span');
        if (!span) {
            span = document.createElement('span');
            dec.appendChild(span);
        }
        span.textContent = text;
        registry.sync();
    }

    private _applyLane(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'lane') return;
        const lane  = ctx.el;
        const label = registry.q<HTMLInputElement>('vtb-fc-lane-label')?.value?.trim() ?? '';
        let labelEl = lane.querySelector<HTMLElement>('.fc-lane-label');
        if (!labelEl) {
            labelEl = document.createElement('span');
            labelEl.className = 'fc-lane-label';
            lane.prepend(labelEl);
        }
        labelEl.textContent = label;
        registry.sync();
    }

    // ─── Cores ────────────────────────────────────────────────────────────────

    private _liveColors(registry: IVtbRegistry): void {
        const ctx  = registry.activeCtx;
        if (!ctx) return;
        const mode = ctx.extra?.mode as FcMode;
        if (mode !== 'node' && mode !== 'decision') return;

        const target = ctx.el;
        const bg = registry.q<HTMLInputElement>('vtb-fc-bg')?.value;
        const bd = registry.q<HTMLInputElement>('vtb-fc-border')?.value;
        const fg = registry.q<HTMLInputElement>('vtb-fc-fg')?.value;

        if (bg) target.style.backgroundColor = bg;
        if (bd) target.style.borderColor      = bd;

        if (mode === 'node') {
            if (fg) target.style.color = fg;
        } else if (mode === 'decision') {
            const span = target.querySelector<HTMLElement>('span');
            if (span && fg) span.style.color = fg;
        }
        registry.sync();
    }

    private _applyColors(registry: IVtbRegistry): void {
        this._liveColors(registry);
    }

    private _resetColors(registry: IVtbRegistry): void {
        const ctx  = registry.activeCtx;
        if (!ctx) return;
        const target = ctx.el;
        const mode   = ctx.extra?.mode as FcMode;

        target.style.removeProperty('background-color');
        target.style.removeProperty('border-color');
        target.style.removeProperty('color');

        if (mode === 'decision') {
            target.querySelector<HTMLElement>('span')?.style.removeProperty('color');
        }
        registry.sync();
    }

    // ─── Estrutura ────────────────────────────────────────────────────────────

    /** Adiciona nó genérico logo APÓS o elemento atual no fluxo. */
    private _addBelow(registry: IVtbRegistry): void {
        const ctx    = registry.activeCtx;
        if (!ctx) return;
        const chart  = ctx.extra?.chart as HTMLElement;
        const target = ctx.el;
        const branch = (ctx.extra?.mode === 'node') ? (ctx.extra?.branch as HTMLElement | null) : null;
        const container = branch || chart;

        const anchor       = directChildOf(container, target) ?? target;
        // Se o próximo irmão já for uma seta, insere após ele
        const insertAfter  = anchor.nextElementSibling?.classList.contains('fc-arrow')
            ? (anchor.nextElementSibling as HTMLElement)
            : anchor;

        const newNode = this._buildNode('fc-process', 'Processo');
        const arrow   = this._buildArrow();
        insertAfter.insertAdjacentElement('afterend', newNode);
        insertAfter.insertAdjacentElement('afterend', arrow);

        registry.sync();
        registry.selectEl(newNode);
    }

    /** Insere um bloco de decisão completo (decision + branch-row + merge + arrow). */
    private _addDecisionBlock(registry: IVtbRegistry): void {
        const ctx   = registry.activeCtx;
        if (!ctx) return;
        const chart  = ctx.extra?.chart as HTMLElement;
        const target = ctx.el;
        const anchor = directChildOf(chart, target) ?? target;

        const insertAfter = anchor.nextElementSibling?.classList.contains('fc-arrow')
            ? (anchor.nextElementSibling as HTMLElement)
            : anchor;

        const blocks = this._buildDecisionBlock();
        // Inserir em ordem inversa para preservar sequência
        for (let i = blocks.length - 1; i >= 0; i--) {
            insertAfter.insertAdjacentElement('afterend', blocks[i]);
        }
        registry.sync();
    }

    /** Adiciona swim lane após o elemento atual. */
    private _addLane(registry: IVtbRegistry): void {
        const ctx    = registry.activeCtx;
        if (!ctx) return;
        const chart  = ctx.extra?.chart as HTMLElement;
        const target = ctx.el;
        const anchor = directChildOf(chart, target) ?? target;

        const lane = document.createElement('div');
        lane.className = 'fc-lane';
        lane.innerHTML = '<span class="fc-lane-label">Raia</span>';
        anchor.insertAdjacentElement('afterend', lane);

        registry.sync();
        registry.selectEl(lane);
    }

    /** Move o elemento selecionado para cima ou para baixo, arrastando a seta adjacente. */
    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx    = registry.activeCtx;
        if (!ctx) return;
        const chart  = ctx.extra?.chart as HTMLElement;
        const target = ctx.el;
        const branch = (ctx.extra?.mode === 'node') ? (ctx.extra?.branch as HTMLElement | null) : null;
        const container = branch || chart;

        const anchor = directChildOf(container, target) ?? target;
        const parent = anchor.parentElement;
        if (!parent) return;

        if (dir === -1) {
            // Subir: move o bloco [anchor] acima do nó logo antes da seta que o precede
            const prevEl = anchor.previousElementSibling;
            const prevPrev = prevEl?.previousElementSibling;
            if (prevPrev) {
                parent.insertBefore(anchor, prevPrev);
                if (prevEl) parent.insertBefore(prevEl, anchor.nextElementSibling ?? null);
            }
        } else {
            // Descer: move o bloco [anchor] abaixo do nó logo após a seta que o segue
            const nextEl   = anchor.nextElementSibling;
            const nextNext = nextEl?.nextElementSibling;
            if (nextNext) {
                const afterNext = nextNext.nextElementSibling ?? null;
                parent.insertBefore(anchor, afterNext);
                if (nextEl) parent.insertBefore(nextEl, anchor);
            }
        }
        registry.sync();
    }

    /** Duplica o elemento selecionado (com seta de conexão). */
    private _duplicate(registry: IVtbRegistry): void {
        const ctx    = registry.activeCtx;
        if (!ctx) return;
        const chart  = ctx.extra?.chart as HTMLElement;
        const target = ctx.el;
        const branch = (ctx.extra?.mode === 'node') ? (ctx.extra?.branch as HTMLElement | null) : null;
        const container = branch || chart;
        const anchor    = directChildOf(container, target) ?? target;

        const clone = anchor.cloneNode(true) as HTMLElement;
        const arrow = this._buildArrow();
        anchor.insertAdjacentElement('afterend', clone);
        anchor.insertAdjacentElement('afterend', arrow);

        registry.sync();
    }

    /** Exclui o elemento selecionado (e a seta precedente, se existir). */
    private _delete(registry: IVtbRegistry): void {
        const ctx    = registry.activeCtx;
        if (!ctx) return;
        const chart  = ctx.extra?.chart as HTMLElement;
        const target = ctx.el;
        const branch = (ctx.extra?.mode === 'node') ? (ctx.extra?.branch as HTMLElement | null) : null;
        const container = branch || chart;
        const anchor    = directChildOf(container, target) ?? target;

        // Remove seta precedente para não deixar seta solta
        const prev = anchor.previousElementSibling;
        if (prev?.classList.contains('fc-arrow')) prev.remove();
        anchor.remove();

        registry.clearSelection();
        registry.sync();
    }

    /** Adiciona elemento ao final do chart (usado no modo "chart"). */
    private _addAtEnd(registry: IVtbRegistry, what: 'node' | 'decision' | 'lane'): void {
        const ctx   = registry.activeCtx;
        if (!ctx) return;
        const chart = ctx.extra?.chart as HTMLElement;
        if (!chart) return;

        if (what === 'node') {
            chart.appendChild(this._buildArrow());
            const node = this._buildNode('fc-process', 'Processo');
            chart.appendChild(node);
            registry.sync();
            registry.selectEl(node);
        } else if (what === 'decision') {
            const blocks = this._buildDecisionBlock();
            for (const b of blocks) chart.appendChild(b);
            registry.sync();
        } else if (what === 'lane') {
            const lane = document.createElement('div');
            lane.className = 'fc-lane';
            lane.innerHTML = '<span class="fc-lane-label">Raia</span>';
            chart.appendChild(lane);
            registry.sync();
            registry.selectEl(lane);
        }
    }

    // ─── Builders (fábrica de elementos) ─────────────────────────────────────

    private _buildNode(type: FcNodeType, text: string): HTMLElement {
        const el = document.createElement('div');
        el.className = 'fc-node ' + type;
        el.innerHTML = type === 'fc-io' ? '<span>' + this._esc(text) + '</span>' : this._esc(text);
        return el;
    }

    private _buildArrow(): HTMLElement {
        const el = document.createElement('div');
        el.className = 'fc-arrow';
        return el;
    }

    /** Gera o bloco completo: decision + branch-row (sim/não) + merge + arrow */
    private _buildDecisionBlock(): HTMLElement[] {
        const decision = document.createElement('div');
        decision.className = 'fc-decision';
        decision.innerHTML = '<span>Condição?</span>';

        const branchRow = document.createElement('div');
        branchRow.className = 'fc-branch-row';
        branchRow.innerHTML =
            '<div class="fc-branch fc-yes">' +
              '<span class="fc-branch-tag">Sim</span>' +
              '<div class="fc-arrow"></div>' +
              '<div class="fc-node fc-process">Ação Sim</div>' +
              '<div class="fc-arrow"></div>' +
            '</div>' +
            '<div class="fc-branch fc-no">' +
              '<span class="fc-branch-tag">Não</span>' +
              '<div class="fc-arrow"></div>' +
              '<div class="fc-node fc-process-red">Ação Não</div>' +
              '<div class="fc-arrow"></div>' +
            '</div>';

        const merge = document.createElement('div');
        merge.className = 'fc-merge';

        const arrow = this._buildArrow();
        return [decision, branchRow, merge, arrow];
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    private _modeLabel(ctx: SmartCtx): string {
        const mode = ctx.extra?.mode as FcMode;
        if (mode === 'decision') return 'Decisão';
        if (mode === 'lane')     return 'Raia';
        if (mode === 'chart')    return 'Fluxograma';
        const node = ctx.el;
        const type = FC_NODE_TYPES.find(t => node.classList.contains(t));
        return type ? FC_NODE_LABELS[type] : 'Nó';
    }

    private _setVal(registry: IVtbRegistry, id: string, val: string): void {
        const el = registry.q<HTMLInputElement | HTMLTextAreaElement>(id);
        if (el) el.value = val;
    }

    private _setColor(registry: IVtbRegistry, id: string, val: string): void {
        const el = registry.q<HTMLInputElement>(id);
        if (el && val.startsWith('#')) el.value = val;
    }

    private _esc(text: string): string {
        const d = document.createElement('div');
        d.textContent = text;
        return d.innerHTML;
    }
}
